import { differentiate } from './derivative';
import { dependsOnVariable, evaluateAt } from './evaluate';
import { type Node } from './parse';
import { rootsAmong, snapNumber as snap, typicalMagnitude } from './roots';

/**
 * The bảng biến thiên — the variation table a Vietnamese maths lesson is built around.
 *
 * ```
 *   x     −∞        −1         1        +∞
 *   y'          +    0    −    0    +
 *   y     −∞  ↗    2   ↘   −2   ↗   +∞
 * ```
 *
 * Three rows: where `x` is, what sign `y'` takes there, and what `y` does in consequence. It
 * is the same information as the graph and it is read in the opposite direction — the table is
 * how a student *derives* the shape before drawing it, which is why a picture of the curve is
 * not a substitute.
 *
 * ## What this module produces, and what it does not
 *
 * A **model**, not a drawing: points, signs and directions, with no coordinate anywhere. The
 * geometry is built from it in `domain/construction/variationTable.ts`, and keeping the two
 * apart is what lets the arithmetic below be tested by asserting on `x^3-3x` rather than on
 * where a line lands.
 *
 * Values arrive as a small tagged union rather than as strings, because two of them cannot be
 * written as text at all: a PDF here is Latin-1, so `∞` would print as `?`. The layout draws
 * it as a curve instead, and it can only do that if it is still told *which* cells are
 * infinite by the time it runs.
 *
 * ## Why the honest failures matter more than the successes
 *
 * `sin(x)` has infinitely many stationary points on ℝ and no finite table. `floor(x)` has no
 * derivative. A tool that produces something anyway — the first twelve of the infinitely many,
 * or a derivative of a staircase — is worse than one that declines, because a teacher will put
 * the result on a worksheet without checking a machine's arithmetic. Every path out of
 * {@link buildVariationTable} that cannot be right returns a reason instead.
 */

/** A cell's content, before anything decides how to draw it. */
export type Quantity =
  | { readonly kind: 'number'; readonly value: number }
  | { readonly kind: 'infinity'; readonly sign: 1 | -1 }
  /** The function has no value here at all: the cell stays empty under a double bar. */
  | { readonly kind: 'none' };

/**
 * What sits in the `y'` row directly beneath a point.
 *
 * The distinction between {@link Zero} and {@link None} is worth spelling out, because
 * conflating them writes a falsehood. `√x` at 0 has a *value* and no *derivative* — the tangent
 * is vertical — so a `0` under that column claims the curve is momentarily flat when it is
 * standing straight up. The column is still there, because the table starts at 0; it simply has
 * nothing to say about `y'`.
 */
export const PointMark = {
  /** The derivative is zero here — a stationary point. */
  Zero: 'zero',
  /** The function is not defined here — a pole. The row is interrupted by a double bar. */
  Undefined: 'undefined',
  /** Nothing is written: an outer end, or an edge where the derivative does not exist. */
  None: 'none',
} as const;
export type PointMark = (typeof PointMark)[keyof typeof PointMark];

export interface VariationPoint {
  /** Where it is. `null` at an infinite end. */
  readonly x: number | null;
  /** What the `x` row says. */
  readonly at: Quantity;
  readonly mark: PointMark;
  /** The limit of `y` approached from the left. `null` at the leftmost point. */
  readonly from: Quantity | null;
  /** The limit of `y` approached from the right. `null` at the rightmost point. */
  readonly to: Quantity | null;
}

/** One open interval between two consecutive points. */
export interface VariationStep {
  /** Whether `y'` is positive across it, which is what the arrow and the sign both say. */
  readonly rising: boolean;
}

export interface VariationTable {
  /** At least two, in increasing order of `x`. */
  readonly points: readonly VariationPoint[];
  /** Exactly one fewer than there are points. */
  readonly steps: readonly VariationStep[];
}

/** Why no table could be built. Codes rather than sentences: the domain layer has no language. */
export const VariationProblem = {
  /** `floor`, `sign`, `min` — no derivative exists, and inventing one asserts a falsehood. */
  NoDerivative: 'no-derivative',
  /** A horizontal line. There is nothing to tabulate. */
  Constant: 'constant',
  /** Periodic, or otherwise past the point where a finite table can say anything. */
  TooManyPoints: 'too-many-points',
  /** Defined nowhere in the range examined. */
  Empty: 'empty',
} as const;
export type VariationProblem = (typeof VariationProblem)[keyof typeof VariationProblem];

export type VariationResult =
  | { readonly ok: true; readonly table: VariationTable }
  | { readonly ok: false; readonly problem: VariationProblem };

export interface VariationOptions {
  /** Left end of the table. `null`, the default, means −∞. */
  readonly from?: number | null;
  /** Right end. `null` means +∞. */
  readonly to?: number | null;
}

/**
 * How far out the scan looks when the table runs to infinity.
 *
 * Fifty rather than the graph's own window: a table is a statement about the whole function,
 * and the stationary points of `x^3 - 27x` sit at ±3 whatever the user happens to be looking
 * at. A run that reaches this edge is treated as reaching infinity, which is what makes the
 * outer columns read ±∞ rather than ±50.
 */
const SCAN_REACH = 50;

/** Samples across the scanned range. At the default reach this is a step of 0.025. */
const SCAN_STEPS = 4000;

/** Interior points beyond which the function is periodic or otherwise not worth tabulating. */
const MAX_INTERIOR_POINTS = 12;

/** Past this, a value is reported as infinite rather than as a number. */
const HUGE = 1e6;

/** Halvings spent locating a boundary. Sixty is float precision on any range. */
const BISECTIONS = 60;

/** Two breakpoints closer than this are the same breakpoint found twice. */
const SAME_POINT = 1e-6;

export function buildVariationTable(f: Node, options: VariationOptions = {}): VariationResult {
  const derivative = differentiate(f);
  if (derivative === null) return { ok: false, problem: VariationProblem.NoDerivative };
  if (!dependsOnVariable(f)) return { ok: false, problem: VariationProblem.Constant };

  const from = options.from ?? null;
  const to = options.to ?? null;
  const scanFrom = from ?? -SCAN_REACH;
  const scanTo = to ?? SCAN_REACH;
  if (!(scanTo > scanFrom)) return { ok: false, problem: VariationProblem.Empty };

  const grid = gridOver(scanFrom, scanTo);
  const runs = domainRuns(f, grid);
  if (runs.length === 0) return { ok: false, problem: VariationProblem.Empty };

  /*
   * Breakpoints are the union of two quite different things: where the function stops being
   * defined, and where its derivative is zero. Both interrupt the `y` row, and neither implies
   * the other — `1/x` has a pole and no stationary point, `x^2` the reverse.
   */
  /*
   * A boundary is an interior column only when it separates *two* runs. The outermost edges —
   * where √x begins, where ln x does — are the ends of the table, and counting them twice puts
   * a column at 0 and then starts the table there as well.
   */
  const boundaries: number[] = [];
  runs.forEach((run, index) => {
    if (index > 0 && run.start !== null) boundaries.push(run.start);
    if (index < runs.length - 1 && run.end !== null) boundaries.push(run.end);
  });

  const stationary = runs.flatMap((run) => rootsIn(derivative, run, grid));

  const interior = dedupe([...boundaries, ...stationary]);
  if (interior.length > MAX_INTERIOR_POINTS) {
    return { ok: false, problem: VariationProblem.TooManyPoints };
  }

  const first = runs[0];
  const last = runs[runs.length - 1];
  if (first === undefined || last === undefined) {
    return { ok: false, problem: VariationProblem.Empty };
  }

  const xs: (number | null)[] = [endOf(first.start, from, -1), ...interior, endOf(last.end, to, 1)];

  const steps = xs.slice(0, -1).map((left, index) => ({
    rising: isRising(derivative, left, xs[index + 1] ?? null),
  }));

  const points = xs.map((x, index) =>
    pointAt(f, derivative, x, index === 0, index === xs.length - 1)
  );

  return { ok: true, table: { points, steps } };

  /**
   * The outer column of the table.
   *
   * `null` means infinity. A run that reaches the edge of the scan is treated as reaching
   * infinity — the alternative is a table that stops at fifty for no reason the reader can
   * see. A run that stops short of it ends where the *function* does, which is how `sqrt(x)`
   * gets a table beginning at 0 rather than at −∞.
   */
  function endOf(edge: number | null, requested: number | null, direction: -1 | 1): number | null {
    if (requested !== null) return requested;
    if (edge === null) return null;

    const reachesTheEdge =
      direction < 0 ? edge <= scanFrom + SAME_POINT : edge >= scanTo - SAME_POINT;
    return reachesTheEdge ? null : edge;
  }
}

/* ---------------------------------------------------------------------- the domain */

interface Run {
  /** Where the function starts being defined. `null` when it reaches the scanned edge. */
  readonly start: number | null;
  readonly end: number | null;
}

function gridOver(from: number, to: number): readonly number[] {
  const step = (to - from) / SCAN_STEPS;
  return Array.from({ length: SCAN_STEPS + 1 }, (_, index) => from + index * step);
}

/**
 * The maximal intervals on which `f` has a value.
 *
 * Two things end a run and they are found differently. **No value** is what `evaluateAt`
 * reports directly, and the boundary is then narrowed by bisection so `sqrt(x)` begins at 0
 * rather than up to one grid step to the right of it. **A pole** has a value on both sides —
 * `1/x` at 0.0001 is enormous but perfectly finite — so it is recognised by a sign flip across
 * a step where the magnitude is far above what the function does elsewhere. A sign flip alone
 * is every zero crossing; a large magnitude alone is `exp`. Both are needed.
 */
function domainRuns(f: Node, grid: readonly number[]): readonly Run[] {
  const values = grid.map((x) => evaluateAt(f, x));
  const scale = typicalMagnitude(values);

  /*
   * The run in progress, carried as its start.
   *
   * `undefined` means there is no run open; `null` means one is open and began at the edge of
   * the scan rather than at a boundary the function has. Two absent-ish values for two
   * genuinely different things, which is what lets the loop below run without a second flag —
   * and a flag mutated from a closure is something neither a reader nor the type checker can
   * follow through the iterations.
   */
  const runs: Run[] = [];
  let start: number | null | undefined;

  for (let index = 0; index < grid.length; index++) {
    const x = grid[index];
    if (x === undefined) continue;

    const here = values[index] ?? null;
    const previous = grid[index - 1];

    if (here === null) {
      // The last defined sample was the one before; find where the run actually stops.
      if (start !== undefined && previous !== undefined) {
        runs.push({ start, end: boundaryBetween(f, previous, x) });
        start = undefined;
      }
      continue;
    }

    if (start === undefined) {
      start = previous === undefined ? null : boundaryBetween(f, x, previous);
      continue;
    }

    // Defined on both sides of the step: only a pole can interrupt the run here.
    const before = values[index - 1] ?? null;
    if (previous !== undefined && before !== null && isPoleBetween(before, here, scale)) {
      const at = poleBetween(f, previous, x, scale);
      runs.push({ start, end: at });
      start = at;
    }
  }

  if (start !== undefined) runs.push({ start, end: null });
  return runs;
}

function isPoleBetween(before: number, after: number, scale: number): boolean {
  return before * after < 0 && Math.min(Math.abs(before), Math.abs(after)) > scale * 30;
}

/**
 * Where a run's edge is, between a sample with a value and one without.
 *
 * `defined` is the side that has one; the answer is the last x on that side, so the table's
 * column sits on the boundary rather than a grid step away from it.
 */
function boundaryBetween(f: Node, defined: number, undefinedAt: number): number {
  let good = defined;
  let bad = undefinedAt;

  for (let i = 0; i < BISECTIONS; i++) {
    const middle = (good + bad) / 2;
    if (evaluateAt(f, middle) === null) bad = middle;
    else good = middle;
  }
  return snap(bad);
}

/** Where a pole is, between two samples that straddle it. */
function poleBetween(f: Node, left: number, right: number, scale: number): number {
  let a = left;
  let b = right;

  for (let i = 0; i < BISECTIONS; i++) {
    const middle = (a + b) / 2;
    const value = evaluateAt(f, middle);
    if (value === null) return snap(middle);

    // Walk towards the side whose magnitude is still growing.
    const leftValue = evaluateAt(f, a);
    if (leftValue !== null && leftValue * value < 0 && Math.abs(value) > scale) b = middle;
    else a = middle;
  }
  return snap((a + b) / 2);
}

/* ----------------------------------------------------------------- stationary points */

/**
 * Where the derivative is zero inside a run.
 *
 * Two kinds, and only the first is what a sign-change search finds. `x^3 - 3x` has its
 * stationary points where `3x^2 - 3` **crosses** zero, and `x^3` has one where `3x^2`
 * merely **touches** it. The touching kind is the one a student is asked about — it is the
 * point where the curve flattens without turning — so a search that only looks for crossings
 * silently omits the interesting half of the syllabus.
 */
function rootsIn(derivative: Node, run: Run, grid: readonly number[]): readonly number[] {
  const from = run.start ?? grid[0] ?? 0;
  const to = run.end ?? grid[grid.length - 1] ?? 0;

  // The run's own samples, taken from the grid the whole scan already uses rather than a fresh
  // one: a touching root is found by the shape of three consecutive samples, so respacing them
  // would move the answer.
  return rootsAmong(
    derivative,
    grid.filter((x) => x > from + SAME_POINT && x < to - SAME_POINT)
  );
}

/* ---------------------------------------------------------------------- reading off */

/**
 * Whether `y` rises across the interval between two points.
 *
 * Probed rather than reasoned about: every sign change of the derivative is already a column,
 * so the sign is constant here and one interior sample settles it. Several are tried because
 * the first may land on a point the function misses — a removable hole, or the exact root that
 * bounds the interval after rounding.
 */
function isRising(derivative: Node, left: number | null, right: number | null): boolean {
  for (const x of probesBetween(left, right)) {
    const value = evaluateAt(derivative, x);
    if (value !== null && value !== 0) return value > 0;
  }
  return true;
}

function probesBetween(left: number | null, right: number | null): readonly number[] {
  if (left === null && right === null) return [0, 1, -1];
  if (left === null) return right === null ? [] : [right - 1, right - 10, right - 0.1];
  if (right === null) return [left + 1, left + 10, left + 0.1];

  const span = right - left;
  return [left + span / 2, left + span / 3, left + (span * 2) / 3, left + span / 10];
}

/**
 * One column.
 *
 * The two one-sided limits are the whole reason a cell is not simply `f(x)`. At a pole the
 * function has *two* values to report and they are what make the table say which way the curve
 * leaves; everywhere else they are equal and the layout draws one cell. Treating both sides
 * uniformly is what keeps `1/x` from needing a special case at every step downstream.
 */
function pointAt(
  f: Node,
  derivative: Node,
  x: number | null,
  isFirst: boolean,
  isLast: boolean
): VariationPoint {
  if (x === null) {
    const sign: 1 | -1 = isFirst ? -1 : 1;
    const limit = limitAtInfinity(f, sign);
    return {
      x: null,
      at: { kind: 'infinity', sign },
      mark: PointMark.None,
      from: isFirst ? null : limit,
      to: isFirst ? limit : null,
    };
  }

  const here = evaluateAt(f, x);
  const defined = here !== null && Math.abs(here) < HUGE;
  const value: Quantity = defined ? { kind: 'number', value: here } : { kind: 'none' };

  const slope = evaluateAt(derivative, x);
  const mark = !defined
    ? PointMark.Undefined
    : slope !== null && Math.abs(slope) < 1e-9
      ? PointMark.Zero
      : PointMark.None;

  return {
    x,
    at: { kind: 'number', value: x },
    mark,
    from: isFirst ? null : defined ? value : limitTowards(f, x, -1),
    to: isLast ? null : defined ? value : limitTowards(f, x, 1),
  };
}

/**
 * The limit of `f` as `x` is approached from one side.
 *
 * Stepped in rather than solved: closer and closer samples, watching whether the magnitude
 * settles or runs away. A value that passes {@link HUGE} while still growing is reported as
 * infinite, which is what the table writes; anything else is the closest sample that had a
 * value, which is the right answer for a removable hole and for the edge of a domain.
 */
function limitTowards(f: Node, x: number, side: 1 | -1): Quantity {
  return classify(
    Array.from({ length: 8 }, (_, index) => evaluateAt(f, x + side * 10 ** -(index + 3)))
  );
}

/** The limit of `f` as `x` runs off to ±∞, by the same stepping-out argument. */
function limitAtInfinity(f: Node, sign: 1 | -1): Quantity {
  return classify(Array.from({ length: 7 }, (_, index) => evaluateAt(f, sign * 10 ** (index + 2))));
}

/**
 * Reading a limit off a sequence of samples marching towards the point.
 *
 * The obvious rule — *big enough means infinite* — is the one that fails, and it fails on the
 * function a lesson on asymptotes is most likely to use. `ln x` at `x = 10⁻¹⁰` is only −23,
 * and there is no reachable step size at which it is a million: `ln(10⁻³⁰⁰)` is −690. A
 * threshold on magnitude would report the limit as −23 and put a finite number where the
 * vertical asymptote goes.
 *
 * What separates the two cases is not how large the samples are but whether they are **still
 * moving**. A sequence approaching a limit takes smaller and smaller steps; a divergent one
 * keeps taking steps of the same size — a constant 2.3 per decade, in the logarithm's case.
 * So the test is on the last two differences, and the magnitude check survives only as a fast
 * path for a pole, where the values run away far quicker than the sequence is long.
 */
function classify(samples: readonly (number | null)[]): Quantity {
  const values = samples.filter((value): value is number => value !== null);
  const last = values[values.length - 1];
  if (last === undefined) return { kind: 'none' };
  if (Math.abs(last) > HUGE) return { kind: 'infinity', sign: last > 0 ? 1 : -1 };

  const [a, b, c] = values.slice(-3);
  if (a !== undefined && b !== undefined && c !== undefined) {
    const first = b - a;
    const second = c - b;
    const stillMoving = first * second > 0 && Math.abs(second) >= Math.abs(first) * 0.5;
    if (stillMoving) return { kind: 'infinity', sign: second > 0 ? 1 : -1 };
  }

  return { kind: 'number', value: last };
}

/* --------------------------------------------------------------------------- tidying */

function dedupe(values: readonly number[]): readonly number[] {
  const sorted = [...values].sort((a, b) => a - b);
  const kept: number[] = [];

  for (const value of sorted) {
    const last = kept[kept.length - 1];
    if (last === undefined || Math.abs(value - last) > SAME_POINT) kept.push(value);
  }
  return kept;
}

/**
 * A number as the table writes it.
 *
 * Two decimals at most, trailing zeros gone. A variation table is read at the back of a
 * classroom, and `-2` carries the lesson where `-2.0000000001` carries nothing at all.
 */
export function formatQuantity(value: number): string {
  if (Object.is(value, -0) || value === 0) return '0';

  const rounded = Math.round(value * 100) / 100;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(2).replace(/0+$/, '');
}
